<template>
    <div>
        <v-container
            class="bg-grey-lighten-5 pr-8 pl-4 mx-auto"
            height="80vh"
            style="max-width: 100%"
        >
            <v-row style="min-width: 600px">
                <v-col class="px-0">
                    <v-card>
                        <v-card-title>Sample Collections by Date</v-card-title>
                        <v-card-text>
                            <v-data-table
                                :headers="samplesByDateHeaders"
                                :items="samplesByDateItems"
                            >
                                <template v-slot:item.actions="{ item }">
                                    <v-row>
                                        <v-icon-btn
                                            color="primary"
                                            @click="viewSamples(item)"
                                            :id="`viewSamples${item.code}${item.date}`"
                                        >
                                            <v-icon>$file-eye</v-icon>
                                            <v-tooltip
                                                location="right"
                                                :activator="`#viewSamples${item.code}${item.date}`"
                                            >
                                                <span>View Data</span>
                                            </v-tooltip>
                                        </v-icon-btn>
                                        <v-icon-btn
                                            color="primary"
                                            @click="downloadSampleDataTemplate(item)"
                                            :id="`downloadSampleDataTemplate${item.code}${item.date}`"
                                            v-if="supportsSampleDataUpload(item)"
                                        >
                                            <v-icon>$table-arrow-down</v-icon>
                                            <v-tooltip
                                                location="right"
                                                :activator="`#downloadSampleDataTemplate${item.code}${item.date}`"
                                            >
                                                <span>Download Sample Data Template</span>
                                            </v-tooltip>
                                        </v-icon-btn>
                                        <v-icon-btn
                                            color="primary"
                                            @click="showUploadSampleDataDialog(item)"
                                            :id="`uploadSampleData${item.code}${item.date}`"
                                            v-if="supportsSampleDataUpload(item)"
                                        >
                                            <v-icon>$table-arrow-up</v-icon>
                                            <v-tooltip
                                                location="right"
                                                :activator="`#uploadSampleData${item.code}${item.date}`"
                                            >
                                                <span>Upload Sample Data</span>
                                            </v-tooltip>
                                        </v-icon-btn>
                                    </v-row>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <v-dialog v-model="showSamplesData">
            <Experiment-Samples
                v-if="showSamplesData"
                :experiment="experiment"
                :date="selectedDate"
                @close="closeSamplesDataDialog"
            />
        </v-dialog>
        <v-dialog v-model="showSampleDataUploadDialog" max-width="600">
            <Upload-Sample-Data
                :requestCode="uploadRequest"
                @close="closeUploadSampleDataDialog"
                @submit="uploadSampleData"
            />
        </v-dialog>
    </div>
</template>

<script>
import { defineComponent, nextTick } from "vue";

import { requestsAPI } from "@/services/api.services";
import config from "../../config";
import { DateTime } from "luxon";
import { mapGetters } from "vuex";
import { withLoadingScreen } from "@/lib/loading";
import {
    getSamplesDataTemplateUrl,
    uploadSampleDataFile,
    workflowSupportsSampleDataTemplate,
} from "@/lib/samplesUtils";
import ExperimentSamples from "./ExperimentSamples.vue";
import UploadSampleData from "../Requests/UploadRequestFile.vue";

export default defineComponent({
    name: "SamplesTab",

    props: {
        experiment: { type: Object },
        // The experiment object looks like:
        // {
        //      code: string,
        //      name: string,
        //      siteTimeZone: string,
        //      day0: DateTime,
        // }
        protocol: { type: Object },
    },

    components: {
        ExperimentSamples,
        UploadSampleData,
    },

    data() {
        return {
            showSamplesData: false,
            selectedDate: null,
            experimentReqs: [],
            outputs: [],
            headers: [],
            rawData: [],
            rawDataSelected: [],
            timeZone: null,
            samplesByDateHeaders: [
                { title: "Date", value: "date", width: "15%" },
                { title: "Request", value: "code", width: "10%" },
                { title: "Request Protocol", value: "name", width: "35%" },
                { title: "Registered Samples", value: "sample_count", width: "15%" },
                { title: "Actions", value: "actions", sortable: false, width: "25%" },
            ],
            showSampleDataUploadDialog: false,
            uploadRequest: null,
        };
    },

    async mounted() {
        await this.loadReqs();
    },

    computed: {
        ...mapGetters(["isAuthorized"]),
        samplesByDateItems: function () {
            let items = [];

            if (this.experimentReqs) {
                this.experimentReqs.forEach((req) => {
                    req.dates.forEach((date) => {
                        items.push({
                            date: DateTime.fromISO(date, { zone: "utc" }).toFormat("yyyy-MM-dd"),
                            code: req.code,
                            name: req.name,
                            sample_count: req.sample_count,
                            req_protocol: req.req_protocol,
                            sample_workflow: req.sample_workflow,
                        });
                    });
                });
            }

            return items.sort((a, b) => {
                const date1 = new Date(a.date);
                const date2 = new Date(b.date);
                return date2 > date1 ? 1 : -1;
            });
        },
    },

    methods: {
        async loadReqs() {
            await withLoadingScreen(async () => {
                this.experimentReqs = await requestsAPI.fetchRequestsByExperiment(
                    this.experiment.code
                );
            });
        },
        downloadSampleDataTemplate(item) {
            const reqProtocol = item.req_protocol.split("_").pop();
            const templateUrl = getSamplesDataTemplateUrl(
                item.code,
                reqProtocol,
                this.$route.params.code
            );
            window.open(templateUrl, "_blank");
        },
        showUploadSampleDataDialog(item) {
            this.uploadRequest = item.code;
            this.showSampleDataUploadDialog = true;
        },
        closeUploadSampleDataDialog() {
            this.showSampleDataUploadDialog = false;
        },
        async uploadSampleData(file) {
            await uploadSampleDataFile(file, this.uploadRequest);
            this.closeUploadSampleDataDialog();
        },
        viewSamples: function (item) {
            this.selectedDate = item.date;
            this.showSamplesData = true;
        },
        closeSamplesDataDialog: function () {
            this.showSamplesData = false;
        },
        supportsSampleDataUpload(item) {
            return workflowSupportsSampleDataTemplate(item.sample_workflow);
        },
    },
});
</script>
